import {
  StructuredOutputNodeType,
  StructuredOutputSchemaNode,
  StructuredOutputSchemaParseResult,
  StructuredOutputSchemaPath,
  UnsupportedSchemaFields,
} from './types';
import {
  collectUnsupportedPaths,
  countUnsupportedFields,
  createDefaultRootSchemaNode,
} from './schemaEditorUtils';

const SUPPORTED_TYPES = new Set<StructuredOutputNodeType>([
  'object',
  'array',
  'string',
  'number',
  'integer',
  'boolean',
]);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatPath = (path: StructuredOutputSchemaPath) => (path.length ? path.join('.') : 'root');

const buildUnsupported = (
  schema: Record<string, unknown>,
  supportedKeys: string[],
): UnsupportedSchemaFields | undefined => {
  const unsupportedEntries = Object.entries(schema).filter(([key]) => !supportedKeys.includes(key));
  return unsupportedEntries.length ? Object.fromEntries(unsupportedEntries) : undefined;
};

const parseNode = (
  rawSchema: unknown,
  path: StructuredOutputSchemaPath,
): { ok: true; value: StructuredOutputSchemaNode } | { ok: false; error: string } => {
  if (!isPlainObject(rawSchema)) {
    return {
      ok: false,
      error: `Schema at ${formatPath(path)} must be a JSON object.`,
    };
  }

  const rawType = rawSchema.type;

  if (typeof rawType !== 'string' || !SUPPORTED_TYPES.has(rawType as StructuredOutputNodeType)) {
    return {
      ok: false,
      error: `Schema at ${formatPath(path)} has unsupported type.`,
    };
  }

  if (rawSchema.description !== undefined && typeof rawSchema.description !== 'string') {
    return {
      ok: false,
      error: `Schema at ${formatPath(path)} must use string description.`,
    };
  }

  switch (rawType) {
    case 'object': {
      const rawProperties = rawSchema.properties;
      const rawRequired = rawSchema.required;

      if (rawProperties !== undefined && !isPlainObject(rawProperties)) {
        return {
          ok: false,
          error: `Object schema at ${formatPath(path)} must use object properties.`,
        };
      }

      if (
        rawRequired !== undefined &&
        (!Array.isArray(rawRequired) || rawRequired.some((item) => typeof item !== 'string'))
      ) {
        return {
          ok: false,
          error: `Object schema at ${formatPath(path)} must use string[] required fields.`,
        };
      }

      const properties: Record<string, StructuredOutputSchemaNode> = {};

      for (const [propertyName, propertySchema] of Object.entries(rawProperties ?? {})) {
        const parsedProperty = parseNode(propertySchema, [...path, propertyName]);

        if (!parsedProperty.ok) {
          return parsedProperty;
        }

        properties[propertyName] = parsedProperty.value;
      }

      const required = rawRequired ? [...rawRequired] : [];
      const invalidRequired = required.filter((fieldName) => !(fieldName in properties));

      if (invalidRequired.length > 0) {
        return {
          ok: false,
          error: `Object schema at ${formatPath(path)} references missing required fields.`,
        };
      }

      return {
        ok: true,
        value: {
          type: 'object',
          description: rawSchema.description,
          properties,
          required,
          unsupported: buildUnsupported(rawSchema, [
            'type',
            'description',
            'properties',
            'required',
          ]),
        },
      };
    }
    case 'array': {
      if (rawSchema.items === undefined) {
        return {
          ok: false,
          error: `Array schema at ${formatPath(path)} must define items.`,
        };
      }

      const parsedItems = parseNode(rawSchema.items, [...path, '[]']);

      if (!parsedItems.ok) {
        return parsedItems;
      }

      return {
        ok: true,
        value: {
          type: 'array',
          description: rawSchema.description,
          items: parsedItems.value,
          unsupported: buildUnsupported(rawSchema, ['type', 'description', 'items']),
        },
      };
    }
    case 'string': {
      if (
        rawSchema.enum !== undefined &&
        (!Array.isArray(rawSchema.enum) || rawSchema.enum.some((item) => typeof item !== 'string'))
      ) {
        return {
          ok: false,
          error: `String schema at ${formatPath(path)} must use string[] enum values.`,
        };
      }

      return {
        ok: true,
        value: {
          type: 'string',
          description: rawSchema.description,
          enum: rawSchema.enum ? [...rawSchema.enum] : undefined,
          unsupported: buildUnsupported(rawSchema, ['type', 'description', 'enum']),
        },
      };
    }
    case 'number':
    case 'integer':
    case 'boolean':
      return {
        ok: true,
        value: {
          type: rawType,
          description: rawSchema.description,
          unsupported: buildUnsupported(rawSchema, ['type', 'description']),
        },
      };
    default:
      return {
        ok: false,
        error: `Schema at ${formatPath(path)} has unsupported type.`,
      };
  }
};

const serializeNode = (node: StructuredOutputSchemaNode): Record<string, unknown> => {
  const base: Record<string, unknown> = {
    ...(node.unsupported ?? {}),
    type: node.type,
  };

  if (node.description) {
    base.description = node.description;
  }

  switch (node.type) {
    case 'object':
      base.properties = Object.fromEntries(
        Object.entries(node.properties).map(([propertyName, propertyNode]) => [
          propertyName,
          serializeNode(propertyNode),
        ]),
      );
      if (node.required.length > 0) {
        base.required = [...node.required];
      }
      return base;
    case 'array':
      base.items = serializeNode(node.items);
      return base;
    case 'string':
      if (node.enum?.length) {
        base.enum = [...node.enum];
      }
      return base;
    default:
      return base;
  }
};

export const parseStructuredOutputSchema = (schema: string): StructuredOutputSchemaParseResult => {
  if (!schema.trim()) {
    return {
      ok: false,
      error: 'Schema string is empty.',
      value: createDefaultRootSchemaNode('object'),
      unsupportedCount: 0,
      unsupportedPaths: [],
    };
  }

  let rawSchema: unknown;

  try {
    rawSchema = JSON.parse(schema);
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Schema is not valid JSON.',
      value: createDefaultRootSchemaNode('object'),
      unsupportedCount: 0,
      unsupportedPaths: [],
    };
  }

  const parsedNode = parseNode(rawSchema, []);

  if (!parsedNode.ok) {
    return {
      ok: false,
      error: parsedNode.error,
      value: createDefaultRootSchemaNode('object'),
      unsupportedCount: 0,
      unsupportedPaths: [],
    };
  }

  const unsupportedCount = countUnsupportedFields(parsedNode.value);
  const unsupportedPaths = collectUnsupportedPaths(parsedNode.value);

  return {
    ok: true,
    value: parsedNode.value,
    unsupportedCount,
    unsupportedPaths,
  };
};

export const serializeStructuredOutputSchema = (value: StructuredOutputSchemaNode) =>
  serializeNode(value);
